import { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "moment/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import styles from "./Calendar.module.css";
import CustomToolbar from "./CustomToolbar";
import MoreTasks from "../../modals/MoreTasks/MoreTasks";
import { saveTask } from "../../services/newdaily";

moment.locale("es");
moment.updateLocale("es", {
  weekdaysShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
});
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const MyCalendar = ({ events, refreshEvents, onDayClick, empleado, isAdmin, onMonthChange, onVisibleMonthChange, selectedDate, }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [modalTasks, setModalTasks] = useState([]);
  const [modalDate, setModalDate] = useState("");
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [calendarKey, setCalendarKey] = useState(0); // Forzar actualización del calendario

  // Sincronizar fechas de los eventos
  useEffect(() => {
    events.forEach((event) => {
      event.start = moment(event.start).toDate();
      event.end = moment(event.end).toDate();
    });
  }, [events]);

  // Forzar actualización del calendario cuando cambian los eventos
  useEffect(() => {
    setCalendarKey((prev) => prev + 1);
  }, [events]);

  useEffect(() => {
    if (onMonthChange && currentDate) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      onMonthChange({ year, month });
    }
  }, [currentDate, onMonthChange]);


  // selección de fecha
  const handleSelectDate = (date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    const tasksForDay = events.filter((event) => event.date === formattedDate);
    onDayClick(tasksForDay, formattedDate);
  };

  const handleSelectEvent = (event) => {
    const formattedDate = moment(event.start).format("YYYY-MM-DD");
    onDayClick([event], formattedDate);
  };

  const buildSavePayloadForDateChange = (event, newDate) => {
    const userId = Number(localStorage.getItem("empleado"));

    const oldDate =
      event?.date ||
      (event?.start ? moment(event.start).format("YYYY-MM-DD") : null);

    const isOtraTask = event?.Tarea === "Otra" || Number(event?.idTarea) === -1;

    const finalTarea = isOtraTask
      ? "Otra"
      : event?.idTarea != null
        ? Number(event.idTarea)
        : event?.Tarea;

    const finalSubtarea = isOtraTask
      ? (event?.Subtarea || "")
      : (event?.idSubtarea != null
          ? Number(event.idSubtarea)
          : Number(event?.idSubtarea || 0));

    const finalProyecto =
      event?.idProyecto != null
        ? Number(event.idProyecto)
        : Number(event?.idProyecto || 0);

    const isProjectionValue = !!event?.is_projection;
    const includeOldDate = isProjectionValue && !!oldDate && oldDate !== newDate;

    const hasValidConfig = (cfg) => {
      if (cfg == null) return false;

      if (Array.isArray(cfg)) return cfg.length > 0;

      if (typeof cfg === "object") return Object.keys(cfg).length > 0;

      if (typeof cfg === "string") {
        const s = cfg.trim();
        if (!s || s === "null" || s === "[]") return false;

        try {
          let parsed = JSON.parse(s);
          if (typeof parsed === "string") parsed = JSON.parse(parsed);

          if (parsed == null) return false;
          if (Array.isArray(parsed)) return parsed.length > 0;
          if (parsed && typeof parsed === "object") {
            return Object.keys(parsed).length > 0;
          }

          return false;
        } catch {
          return false;
        }
      }

      return false;
    };

    const isPeriodicTask =
      hasValidConfig(event?.config_json) ||
      !!event?.is_projection ||
      (event?.parent_periodic_id !== null && event?.parent_periodic_id !== undefined);

    return {
      taskId: event.id,
      userId,
      Tarea: finalTarea,
      Subtarea: finalSubtarea,
      Proyecto: finalProyecto,
      Fecha: newDate,
      Minutos: Number(event?.Minutos) || 0,
      Inicio: event?.Inicio || "",
      Descripcion: event?.Descripcion || "",
      Notas: event?.Notas || "",
      endDate: event?.endDate ?? event?.end_date ?? null,
      config_json: event?.config_json ?? [],
      is_projection: isProjectionValue,
      ...(includeOldDate ? { oldDate } : {}),
      ...(isPeriodicTask ? { apply_all: false } : {}),
    };
  };

  const handleEventDrop = useCallback(
    async ({ event, start }) => {
      const newDate = moment(start).format("YYYY-MM-DD");

      try {
        const payload = buildSavePayloadForDateChange(event, newDate);

        console.log("📦 saveTask (drag inside) payload →", payload);

        const result = await saveTask(payload);

        if (result?.OK === 1) {
          await refreshEvents();
          setCalendarKey((prev) => prev + 1);
        } else {
          console.error("❌ Error al guardar la tarea en el servidor.", result);
        }
      } catch (error) {
        console.error("❌ Error en saveTask:", error);
      }
    },
    [refreshEvents]
  );

  // Manejar "+x Más"
  const handleShowMore = (eventsForDay, date) => {
    const dayNumberStr = moment(date).format("DD");
    const dayNumber = parseInt(dayNumberStr, 10);

    const dayButton = [...document.querySelectorAll(".rbc-button-link")].find(
      (btn) => btn.textContent.trim() === dayNumberStr
    );

    if (dayButton) {
      const rect = dayButton.getBoundingClientRect();
      let baseX = rect.left + window.scrollX;
      let baseY = rect.bottom + window.scrollY;
      const adjustedY = dayNumber >= 24 ? baseY + 320 : baseY + 60;
      setPopupPosition({ x: baseX, y: adjustedY });
    } else {
      setPopupPosition({ x: window.innerWidth / 3, y: 200 });
    }

    setModalTasks(eventsForDay);
    setModalDate(moment(date).format("DD/MM/YYYY"));
    setShowModal(true);
  };

  // Manejar arrastre desde el modal
  const handleDragStartFromPopup = (event) => {
    setDraggedEvent(event);
  };

  const dragFromOutsideItem = useCallback(() => draggedEvent, [draggedEvent]);

  // Manejar Drop desde el modal al calendario
  const onDropFromOutside = useCallback(
    async ({ start }) => {
      if (!draggedEvent) return;

      const newDate = moment(start).format("YYYY-MM-DD");

      try {
        const payload = buildSavePayloadForDateChange(draggedEvent, newDate);

        console.log("📦 saveTask (drag from popup) payload →", payload);

        const result = await saveTask(payload);

        if (result?.OK === 1) {
          localStorage.setItem("currentCalendarDate", newDate);
          setShowModal(false);
          await refreshEvents();
          setCalendarKey((prev) => prev + 1);
        } else {
          console.error("❌ Error al guardar la tarea en el servidor.", result);
        }
      } catch (error) {
        console.error("❌ Error en saveTask:", error);
      } finally {
        setDraggedEvent(null);
      }
    },
    [draggedEvent, refreshEvents]
  );

  const messages = {
    today: "Hoy",
    previous: "Anterior",
    next: "Siguiente",
    month: "Mes",
    week: "Semana",
    showMore: (total) => `${total} más`,
  };

  return (
    <div className={styles.calendarContainer}>
      <DnDCalendar
        key={calendarKey}
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: "100%" }}
        views={{ month: true, week: true }}
        messages={messages}
        defaultView={Views.MONTH}
        date={currentDate}
        onNavigate={(newDate) => {
          setCurrentDate(newDate);
          const month = moment(newDate).month() + 1;
          const year = moment(newDate).year();
          onVisibleMonthChange && onVisibleMonthChange({ month, year });
        }}
        selectable
        onSelectSlot={({ start }) => handleSelectDate(start)}
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventDrop}
        onDropFromOutside={onDropFromOutside}
        dragFromOutsideItem={dragFromOutsideItem}
        onShowMore={(events, date) => handleShowMore(events, date)}
        components={{
          toolbar: (toolbarProps) => (
            <CustomToolbar {...toolbarProps} isAdmin={isAdmin} />
          ),
          event: ({ event }) => (
            <div
              data-testid="calendar-event"
              data-event-subtask={event.Subtarea || ""}
              data-event-task={event.Tarea || ""}
              data-event-project={event.Proyecto || ""}
              className={styles.eventContainer}
            >
              <span className={styles.eventDot} style={{ backgroundColor: event.color }}></span>
              <span
                className={styles.eventText}
                style={event.Hecho === 1 ? { textDecoration: "line-through" } : {}}
                title={event.Subtarea || "Sin subtarea"}
              >
                {event.Subtarea || "Sin subtarea"}
              </span>
              {event.start && moment(event.start).format("HH:mm") !== "00:00" && (
                <span className={styles.eventTime}>
                  {moment(event.start).format("HH:mm")}
                </span>
              )}
            </div>
          ),
        }}
        eventPropGetter={() => ({
          style: {
            backgroundColor: "transparent",
            padding: "1px",
            marginLeft: "5px",
            marginBottom: "1px",
          },
        })}
        dayPropGetter={(date) => {
          const formattedDate = moment(date).format("YYYY-MM-DD");
          return formattedDate === selectedDate ? { className: styles.selectedDay } : {};
        }}
        resizable
      />

      <MoreTasks
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        tasks={modalTasks}
        date={modalDate}
        position={popupPosition}
        onDragStart={handleDragStartFromPopup}
      />
    </div>
  );
};

export default MyCalendar;
